import React from 'react';
import { createSlice, Draft } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '@/store';
import { useAppDispatch } from '@/store/hooks';
import { useQueryId, useSeriesId } from '@/hooks';
import { UUID } from '@/types';
import {
  AGGREGATE_CONFIGURATION_CHANGED,
  LAYER_USER_INTERACTION,
  MODAL_SAVE_CLICKED,
  SAVED_QUERY_SELECTED,
  SAVED_QUERY_UPDATED,
  SEARCH_LOADED,
  START_NEW_QUERY,
  TABLE_SELECTED,
  TEMPLATE_LAYER_ADDED,
  VIEW_DELETED,
  VIEW_SAVED,
} from '@/actions/constants';

type State = {
  lastVisitedURL: Maybe<string>;
  getStarted: boolean;
  queryStartTime: Maybe<number>;
  queryExecutionTime: Maybe<string>;
  savedQuerySeriesId: Maybe<UUID>;
  fullResultsSeriesId: Maybe<UUID>;
  chartConfigurationHasChanged: boolean;
  fullResultsLoaded: boolean;
  page: number;
  isLoadingSteps: boolean;
  isLoadingColumns: boolean;
  isLoadingRows: boolean;
  isLoadingRowsCount: boolean;
  latestQueryMeta: Maybe<{ seriesId: UUID } | { queryId: UUID }>;
};

const initialState: State = {
  lastVisitedURL: null,
  getStarted: true,
  queryStartTime: null,
  queryExecutionTime: null,
  savedQuerySeriesId: null,
  fullResultsSeriesId: null,
  fullResultsLoaded: false,
  chartConfigurationHasChanged: false,
  page: 1,
  isLoadingSteps: false,
  isLoadingColumns: false,
  isLoadingRows: false,
  isLoadingRowsCount: false,
  latestQueryMeta: null,
};
const reset = (state: Draft<State>) => {
  state.page = initialState.page;
  state.savedQuerySeriesId = initialState.savedQuerySeriesId;
  state.fullResultsSeriesId = initialState.fullResultsSeriesId;
  state.fullResultsLoaded = initialState.fullResultsLoaded;
  state.chartConfigurationHasChanged =
    initialState.chartConfigurationHasChanged;
};

export const queryBuilderSlice = createSlice({
  name: 'queryBuilderSlice',
  initialState,
  reducers: {
    setGetStarted: (state, action: PayloadAction<State['getStarted']>) => {
      state.getStarted = action.payload;
    },
    startQueryTimer: (state) => {
      state.queryStartTime = Date.now();
      state.queryExecutionTime = null;
    },
    setQueryExecutionTime: (
      state,
      action: PayloadAction<NonNullable<State['queryStartTime']>>,
    ) => {
      state.queryExecutionTime = `${(
        (action.payload - state.queryStartTime!) /
        1000
      ).toFixed(1)}0`;
      state.queryStartTime = null;
    },
    setLastVisitedURL: (
      state,
      action: PayloadAction<State['lastVisitedURL']>,
    ) => {
      state.lastVisitedURL = action.payload;
    },
    setSavedQuerySeriesId: (
      state,
      action: PayloadAction<State['savedQuerySeriesId']>,
    ) => {
      state.savedQuerySeriesId = action.payload;
    },
    setPage: (state, action: PayloadAction<State['page']>) => {
      state.page = action.payload;
    },
    setFullResultsSeriesId: (
      state,
      action: PayloadAction<State['fullResultsSeriesId']>,
    ) => {
      state.fullResultsSeriesId = action.payload;
    },
    setFullResultsLoaded: (
      state,
      action: PayloadAction<State['fullResultsLoaded']>,
    ) => {
      state.fullResultsLoaded = action.payload;
    },
    stepsStartLoading: (state) => {
      state.isLoadingSteps = true;
      state.isLoadingColumns = true;
      state.isLoadingRows = true;
      state.isLoadingRowsCount = true;
    },
    rowsStartLoading: (state) => {
      state.isLoadingRows = true;
      state.isLoadingRowsCount = true;
    },
    stepsStopLoading: (state) => {
      state.isLoadingSteps = false;
    },
    columnsStopLoading: (state) => {
      state.isLoadingColumns = false;
    },
    rowsStopLoading: (state) => {
      state.isLoadingRows = false;
    },
    rowsCountStopLoading: (state) => {
      state.isLoadingRowsCount = false;
    },
    updateLatestQueryMeta: (
      state,
      action: PayloadAction<NonNullable<State['latestQueryMeta']>>,
    ) => {
      state.latestQueryMeta = action.payload;
    },
  },
  extraReducers: {
    [VIEW_DELETED]: (state, action) => {
      const deletedQuerySeriesId = action.payload.deleted._series;
      if (state.lastVisitedURL?.includes(deletedQuerySeriesId)) {
        state.lastVisitedURL = null;
      }
    },
    [VIEW_SAVED]: (state, action) => {
      state.savedQuerySeriesId = action.payload.savedView._series;
    },
    [START_NEW_QUERY]: (state) => {
      state.getStarted = false;
    },
    [TABLE_SELECTED]: reset,
    [SAVED_QUERY_SELECTED]: reset,
    [SAVED_QUERY_UPDATED]: reset,
    [SEARCH_LOADED]: reset,
    [TEMPLATE_LAYER_ADDED]: reset,
    [MODAL_SAVE_CLICKED]: reset,
    [LAYER_USER_INTERACTION]: (state, action) => {
      if (
        ['toggled', 'deleted', 'remove', 'updated'].includes(
          action.payload.event,
        )
      ) {
        state.page = initialState.page;
        state.fullResultsSeriesId = initialState.fullResultsSeriesId;
        state.fullResultsLoaded = initialState.fullResultsLoaded;
      }

      if (action.payload.event === 'update-charting-options') {
        state.chartConfigurationHasChanged = true;
      }
    },
    [AGGREGATE_CONFIGURATION_CHANGED]: (state) => {
      state.chartConfigurationHasChanged = true;
    },
  },
});

export const selectLatestQueryMeta = (state: RootState) =>
  state.queryBuilderSlice.latestQueryMeta!;
export const selectGetStarted = (state: RootState) =>
  state.queryBuilderSlice.getStarted;

export const selectQueryStartTime = (state: RootState) =>
  state.queryBuilderSlice.queryStartTime;
export const selectQueryExecutionTime = (state: RootState) =>
  state.queryBuilderSlice.queryExecutionTime;

export const selectSavedQuerySeriesId = (state: RootState) =>
  state.queryBuilderSlice.savedQuerySeriesId;

export const selectPage = (state: RootState) => state.queryBuilderSlice.page;

export const selectLastVisitedURL = (state: RootState) =>
  state.queryBuilderSlice.lastVisitedURL;

export const selectFullResultsSeriesId = (state: RootState) =>
  state.queryBuilderSlice.fullResultsSeriesId;

export const selectIsFullResultsLoaded = (state: RootState) =>
  state.queryBuilderSlice.fullResultsLoaded;

export const selectChartConfigurationHasChanged = (state: RootState) =>
  state.queryBuilderSlice.chartConfigurationHasChanged;

export const selectIsLoadingSteps = (state: RootState) =>
  state[queryBuilderSlice.name].isLoadingSteps;

export const selectIsLoadingColumns = (state: RootState) =>
  state[queryBuilderSlice.name].isLoadingSteps ||
  state[queryBuilderSlice.name].isLoadingColumns;

export const selectIsLoadingRows = (state: RootState) =>
  state[queryBuilderSlice.name].isLoadingSteps ||
  state[queryBuilderSlice.name].isLoadingColumns ||
  state[queryBuilderSlice.name].isLoadingRows;

export const selectIsLoadingRowsCount = (state: RootState) =>
  state[queryBuilderSlice.name].isLoadingSteps ||
  state[queryBuilderSlice.name].isLoadingColumns ||
  state[queryBuilderSlice.name].isLoadingRows ||
  state[queryBuilderSlice.name].isLoadingRowsCount;

export const {
  setLastVisitedURL,
  setGetStarted,
  startQueryTimer,
  setQueryExecutionTime,
  setPage,
  setFullResultsSeriesId,
  setFullResultsLoaded,
  stepsStartLoading,
  rowsStartLoading,
  stepsStopLoading,
  columnsStopLoading,
  rowsStopLoading,
  rowsCountStopLoading,
  updateLatestQueryMeta,
} = queryBuilderSlice.actions;

export const useUpdateLatestQueryMeta = () => {
  const dispatch = useAppDispatch();
  const seriesId = useSeriesId();
  const queryId = useQueryId();

  React.useEffect(() => {
    if (seriesId) {
      dispatch(updateLatestQueryMeta({ seriesId }));
    } else if (queryId) {
      dispatch(updateLatestQueryMeta({ queryId }));
    }
  }, [seriesId, queryId]);
};
